import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledDataGrid } from '../UserRequestsStyles';
import { buildSupportColumns } from './columns';
import { SupportRequest } from '../types';
import { GridSortModel, ToolbarPropsOverrides } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import UserRequestsToolbar from '../UserRequestsToolbar';
import { usePagination } from '../../../shared/hooks/usePagination';
import { useSupportRequests, mapSortFieldToBackend } from '../hooks/useSupportRequests';
import { useUserDateFormatter } from '../hooks';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../shared/api/getApiErrorMessage';

const SupportRequests = () => {
  const navigate = useNavigate();
  const { formatDateTime: formatDate } = useUserDateFormatter();
  const { enqueueSnackbar } = useSnackbar();

  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    filters,
    quickFilter,
    handleFilterModelChange,
    handlePagination,
  } = usePagination({ initialPageSize: 10 });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'status', sort: 'asc' },
  ]);

  const supportSort = useMemo(
    () =>
      sortModel
        .filter(item => item.field && item.sort)
        .flatMap(item => {
          const direction = item.sort === 'asc' ? ('ASC' as const) : ('DESC' as const);
          return mapSortFieldToBackend(item.field, direction);
        }),
    [sortModel],
  );

  const {
    data: supportRequests,
    isLoading,
    isFetching,
    isError,
    error,
  } = useSupportRequests({
    pageSize,
    page: pageIndex,
    filters,
    search: quickFilter,
    sort: supportSort,
  });

  const rows = useMemo<SupportRequest[]>(
    () => supportRequests?.elements || [],
    [supportRequests],
  );
  const totalRows = supportRequests?.total || 0;

  useEffect(() => {
    if (!isError) return;

    enqueueSnackbar(getApiErrorMessage(error, 'Failed to load support requests'), {
      variant: 'error',
    });
  }, [enqueueSnackbar, error, isError]);

  const renderToolbar = useCallback(
    (props: ToolbarPropsOverrides) => (
      <UserRequestsToolbar
        {...props}
        total={totalRows}
        label="Support Requests"
      />
    ),
    [totalRows],
  );

  return (
    <StyledDataGrid
      rows={rows}
      columns={buildSupportColumns(formatDate)}
      loading={isLoading || isFetching}
      pageSizeOptions={[10, 25, 50]}
      paginationMode="server"
      filterMode="server"
      sortingMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={handlePagination}
      rowCount={totalRows}
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      slots={{ toolbar: renderToolbar }}
      showToolbar
      disableRowSelectionOnClick
      onRowClick={params => navigate(`/user-requests/support/${params.row.id}`)}
      getRowClassName={() => 'clickable'}
      initialState={{
        sorting: {
          sortModel: [{ field: 'status', sort: 'asc' }],
        },
      }}
    />
  );
};

export default SupportRequests;
